/**
 * Iter XX.243 · Gully Pulse — /pulse feed page + share composer +
 * moment cards + inline follow/unfollow + notifications inbox
 * dropdown. Kept intentionally single-file for MVP scope.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchPulseFeed, shareMoment, deleteMoment, celebrateMoment,
  followUser, unfollowUser,
  fetchNotifications, markNotificationsSeen,
  fetchPulseEditableProfile,
} from "../lib/pulse";
import PeopleDiscovery from "../components/PeopleDiscovery";
import PulseProfileEditor from "../components/PulseProfileEditor";
// Iter XX.267a — canonical mobile bottom nav on non-Chat primary
// surfaces. Fixes: (a) Pulse rendering with no way back to Chat, (b)
// central Gully tap being a no-op from Pulse.
import PrimarySurfaceNav from "../components/PrimarySurfaceNav";
// Iter XX.267c — page error boundary so a render exception on iOS
// WKWebView surfaces visible text instead of a black screen.
import PageErrorBoundary from "../components/PageErrorBoundary";
// Iter XX.272 — Gully Challenge #001 discovery card. Renders only
// when the backend permits exposure (feature flag ON AND challenge
// doc enabled=True). Both gates enforced server-side.
import ChallengeCard from "../components/ChallengeCard";
import { fetchCurrentChallenge } from "../lib/challenge";

const KIND_OPTIONS = [
  { key: "achievement", label: "Achievement", emoji: "🔥",
    placeholder: "5 wickets today" },
  { key: "practice",    label: "Practice",    emoji: "🏏",
    placeholder: "Completed another training session" },
  { key: "milestone",   label: "Milestone",   emoji: "💯",
    placeholder: "Made the squad" },
  { key: "support",     label: "Support",     emoji: "🤝",
    placeholder: "Working through a rough patch — send some 🔥" },
];

function ShareComposer({ onShared, prefill }) {
  const [kind, setKind] = useState("achievement");
  const [title, setTitle] = useState(prefill || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const active = KIND_OPTIONS.find((k) => k.key === kind);
  useEffect(() => {
    if (prefill) setTitle(prefill);
  }, [prefill]);

  const submit = async () => {
    if (busy) return;
    if (!title.trim()) { setErr("Add a short title."); return; }
    setBusy(true); setErr("");
    const res = await shareMoment({ kind, title: title.trim() });
    setBusy(false);
    if (res?.error) { setErr(res.error); return; }
    setTitle("");
    onShared && onShared(res);
  };

  return (
    <div
      data-testid="pulse-composer"
      className="rounded-3xl border border-neutral-800 bg-neutral-900/70 p-5 mb-6"
    >
      <div className="text-[11px] uppercase tracking-widest text-emerald-500 mb-3">
        Share to Gully
      </div>
      <div className="flex gap-2 flex-wrap mb-3">
        {KIND_OPTIONS.map((k) => (
          <button
            key={k.key}
            data-testid={`pulse-composer-kind-${k.key}`}
            onClick={() => setKind(k.key)}
            className={
              "px-3 py-1.5 rounded-full text-[13px] transition-colors border " +
              (kind === k.key
                ? "bg-emerald-500 text-neutral-950 border-emerald-500"
                : "border-neutral-800 text-neutral-300 hover:bg-neutral-800")
            }
          >
            {k.emoji} {k.label}
          </button>
        ))}
      </div>
      <textarea
        data-testid="pulse-composer-title"
        rows={2}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={active.placeholder}
        maxLength={120}
        className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-[15px] text-neutral-100 focus:outline-none focus:border-emerald-800 resize-none"
      />
      {err && (
        <div data-testid="pulse-composer-error" className="text-[13px] text-rose-400 mt-2">
          {err}
        </div>
      )}
      <div className="flex justify-end mt-3">
        <button
          data-testid="pulse-composer-submit"
          onClick={submit}
          disabled={busy || !title.trim()}
          className="px-5 py-2 rounded-full bg-emerald-500 text-neutral-950 font-medium hover:bg-emerald-400 transition-colors disabled:opacity-40"
        >
          {busy ? "Sharing…" : "Share to Gully"}
        </button>
      </div>
      <p className="text-[12px] text-neutral-500 mt-3 leading-relaxed">
        Nothing gets shared automatically. Only what you post here becomes public.
      </p>
    </div>
  );
}

function MomentCard({ item, viewerId, onDelete, onCelebrated, onOpenProfile }) {
  const isSupport = item.kind === "support";
  const emoji = isSupport ? "🤝" : item.kind === "practice" ? "🏏"
    : item.kind === "milestone" ? "💯" : "🔥";
  const isSelf = !!item.viewer?.is_self;
  const reacted = new Set(item.viewer?.reactions || []);
  const [count, setCount] = useState(item.celebrations_count || 0);
  const [busy, setBusy] = useState(false);

  const react = async (reaction) => {
    if (busy || reacted.has(reaction)) return;
    setBusy(true);
    const res = await celebrateMoment(item.moment_id, reaction);
    setBusy(false);
    if (res?.error) return;
    reacted.add(reaction);
    if (reaction === "fire") setCount((c) => c + (res?.duplicate ? 0 : 1));
    onCelebrated && onCelebrated();
  };

  return (
    <article
      data-testid="pulse-moment-card"
      className={
        "rounded-3xl border p-5 mb-4 " +
        (isSupport
          ? "border-sky-900/60 bg-sky-900/10"
          : "border-neutral-800 bg-neutral-900/70")
      }
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <button
          data-testid="pulse-moment-author"
          onClick={() => onOpenProfile(item.author.user_id)}
          className="flex items-center gap-3 text-left"
        >
          <div className="w-9 h-9 rounded-full bg-neutral-800 flex items-center justify-center text-neutral-300 font-medium">
            {(item.author.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div>
            <div className="text-[14px] text-neutral-100 font-medium leading-tight">
              {item.author.name}
            </div>
            {item.author.role && (
              <div className="text-[12px] text-neutral-500">
                {item.author.role}
              </div>
            )}
          </div>
        </button>
        {isSelf && (
          <button
            data-testid="pulse-moment-delete"
            onClick={() => onDelete(item.moment_id)}
            className="text-[12px] text-neutral-500 hover:text-rose-400 transition-colors"
          >
            Delete
          </button>
        )}
      </div>

      <div
        data-testid="pulse-moment-title"
        className="text-[18px] leading-snug text-neutral-100 font-medium mb-1"
      >
        {emoji} {item.title}
      </div>
      {item.body && (
        <div className="text-[14px] text-neutral-300 mb-2">{item.body}</div>
      )}

      <div className="flex items-center gap-3 mt-3">
        {isSupport ? (
          <>
            {[
              { key: "back_yourself", label: "🔥 Back yourself" },
              { key: "keep_going",    label: "👊 Keep going" },
              { key: "next_one",      label: "🏏 Next one" },
            ].map((r) => (
              <button
                key={r.key}
                data-testid={`pulse-moment-support-${r.key}`}
                onClick={() => react(r.key)}
                disabled={busy || reacted.has(r.key) || isSelf}
                className={
                  "px-3 py-1.5 rounded-full text-[13px] border transition-colors " +
                  (reacted.has(r.key)
                    ? "bg-sky-500 border-sky-500 text-neutral-950"
                    : "border-neutral-800 text-neutral-300 hover:bg-neutral-800 disabled:opacity-40")
                }
              >
                {r.label}
              </button>
            ))}
          </>
        ) : (
          <button
            data-testid="pulse-moment-celebrate"
            onClick={() => react("fire")}
            disabled={busy || reacted.has("fire") || isSelf}
            className={
              "px-3 py-1.5 rounded-full text-[13px] border transition-colors " +
              (reacted.has("fire")
                ? "bg-emerald-500 border-emerald-500 text-neutral-950"
                : "border-neutral-800 text-neutral-300 hover:bg-neutral-800 disabled:opacity-40")
            }
          >
            🔥 Celebrate
          </button>
        )}
        <div
          data-testid="pulse-moment-count"
          className="text-[13px] text-neutral-500"
        >
          {count > 0 ? `${count} celebration${count > 1 ? "s" : ""}` : "Be the first"}
        </div>
      </div>
    </article>
  );
}

function NotificationsBell({ onOpenProfile }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const reload = async () => setData(await fetchNotifications());
  useEffect(() => {
    reload();
    const t = setInterval(reload, 30_000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative">
      <button
        data-testid="pulse-notifications-bell"
        onClick={async () => { setOpen(!open); if (!open && data.unread) { await markNotificationsSeen(); reload(); } }}
        className="relative text-[13px] text-neutral-300 hover:text-neutral-100 px-2 py-1"
      >
        Inbox
        {data.unread > 0 && (
          <span
            data-testid="pulse-notifications-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-[10px] text-neutral-950 flex items-center justify-center"
          >
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="pulse-notifications-panel"
          className="absolute right-0 top-9 w-80 max-h-96 overflow-y-auto bg-neutral-950 border border-neutral-800 rounded-2xl p-2 z-40 shadow-2xl"
        >
          {data.items.length === 0 && (
            <div className="text-[13px] text-neutral-500 p-3">
              Nothing yet. When people follow you or celebrate your moments, they'll show up here.
            </div>
          )}
          {data.items.map((n) => (
            <button
              key={n.notification_id}
              onClick={() => n.actor?.user_id && onOpenProfile(n.actor.user_id)}
              className="w-full text-left p-3 rounded-xl hover:bg-neutral-900 transition-colors"
            >
              <div className="text-[13px] text-neutral-100">
                {n.kind === "followed" && <>👤 <b>{n.actor?.name || "Someone"}</b> started following you.</>}
                {n.kind === "celebrated" && <>🔥 <b>{n.actor?.name || "Someone"}</b> celebrated your moment.</>}
                {n.kind === "new_moment" && <>🏏 <b>{n.actor?.name || "Someone you follow"}</b> shared cricket: <i>{n.extra?.title || ""}</i></>}
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">
                {new Date(n.created_at).toLocaleString()}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PulsePage() {
  return (
    <PageErrorBoundary pageName="Pulse">
      <PulsePageInner />
    </PageErrorBoundary>
  );
}

function PulsePageInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = new URLSearchParams(location.search).get("prefill") || "";
  const [feed, setFeed] = useState({ items: [], is_discover: false });
  const [reloadTs, setReloadTs] = useState(0);
  // XX.243b — public identity + onboarding state.
  const [pubProfile, setPubProfile] = useState(null);
  const [editing,    setEditing]    = useState(false);
  const [justCreated, setJustCreated] = useState(false);
  const [inviteDismissed, setInviteDismissed] = useState(false);
  // Iter XX.272 — Challenge state. `available: false` when hidden;
  // ChallengeCard collapses to null in that case.
  const [challengeState, setChallengeState] = useState(null);
  useEffect(() => {
    (async () => setChallengeState(await fetchCurrentChallenge()))();
  }, [reloadTs]);

  const reloadPubProfile = async () => {
    const p = await fetchPulseEditableProfile();
    setPubProfile(p);
  };
  useEffect(() => { reloadPubProfile(); }, []);
  useEffect(() => {
    (async () => setFeed(await fetchPulseFeed()))();
  }, [reloadTs]);

  const openProfile = (userId) => navigate(`/pulse/u/${userId}`);

  return (
    <div data-testid="pulse-page" className="min-h-screen bg-neutral-950 text-neutral-100">
      <div
        className="mx-auto max-w-2xl px-5 pb-24"
        style={{
          // Iter XX.267a — respect the iOS status bar / Dynamic Island
          // so the "← Back to chat / Inbox" row doesn't collide with
          // the notch on physical iPhones. Bottom padding leaves room
          // for the fixed <MobileBottomNav>.
          paddingTop: "calc(1.5rem + env(safe-area-inset-top, 0px))",
          paddingBottom: "calc(6rem + env(safe-area-inset-bottom, 0px))",
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <button
            data-testid="pulse-back"
            onClick={() => navigate("/chat")}
            className="text-[13px] text-neutral-500 hover:text-neutral-300"
          >
            ← Back to chat
          </button>
          <div className="flex items-center gap-3">
            {/* XX.243c-hotfix — permanent profile CTA. ALWAYS
                visible for authenticated users, regardless of
                suggestion state or prior banner dismissals. */}
            {pubProfile && (
              <button
                data-testid="pulse-profile-cta"
                onClick={() => setEditing(true)}
                className={
                  "px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors " +
                  (pubProfile.has_created_profile
                    ? "border border-neutral-700 text-neutral-200 hover:bg-neutral-800"
                    : "bg-emerald-500 text-neutral-950 hover:bg-emerald-400")
                }
              >
                {pubProfile.has_created_profile
                  ? "Edit my cricket profile"
                  : "Create my cricket profile"}
              </button>
            )}
            <NotificationsBell onOpenProfile={openProfile} />
          </div>
        </div>
        <h1 data-testid="pulse-title" className="text-3xl font-medium mb-1">Gully Pulse</h1>
        <p data-testid="pulse-subtitle" className="text-[14px] text-neutral-400 mb-6">
          Cricket, from the people you follow. Share your moments and cheer on theirs.
        </p>

        {/* XX.243b — Onboarding banner. Never blocks Pulse; user
            can dismiss and still use the feed / discovery. */}
        {pubProfile && !pubProfile.has_created_profile && !editing && !inviteDismissed && (
          <div
            data-testid="pulse-onboarding-invite"
            className="rounded-3xl border border-emerald-900/60 bg-emerald-900/10 p-5 mb-6"
          >
            <div className="text-[11px] uppercase tracking-widest text-emerald-500 mb-2">
              Join the Gully cricket community
            </div>
            <div className="text-[18px] text-neutral-100 font-medium leading-snug mb-2">
              Follow cricketers. Celebrate performances. Share your journey.
            </div>
            <p className="text-[13px] text-neutral-400 mb-4">
              Create a short public cricket identity so other cricketers can find you.
              Your AI conversations and private cricket data stay private.
            </p>
            <div className="flex gap-3">
              <button
                data-testid="pulse-onboarding-cta"
                onClick={() => setEditing(true)}
                className="px-5 py-2.5 rounded-full bg-emerald-500 text-neutral-950 font-medium hover:bg-emerald-400 transition-colors"
              >
                Create my cricket profile
              </button>
              <button
                data-testid="pulse-onboarding-skip"
                onClick={() => setInviteDismissed(true)}
                className="px-4 py-2.5 rounded-full text-neutral-400 hover:text-neutral-200 transition-colors"
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        {editing && (
          <PulseProfileEditor
            onCancel={() => setEditing(false)}
            onSaved={(saved) => {
              setEditing(false);
              setPubProfile(saved);
              if (saved?.has_created_profile) setJustCreated(true);
            }}
          />
        )}

        {/* XX.243b — Post-setup activation prompt: "Find your first
            cricketers". Auto-shows for a single render pass. */}
        {justCreated && !editing && (
          <div
            data-testid="pulse-post-setup-prompt"
            className="rounded-3xl border border-emerald-900/40 bg-emerald-900/10 p-5 mb-6"
          >
            <div className="text-[18px] text-neutral-100 font-medium mb-1">
              Find your first cricketers
            </div>
            <div className="text-[13px] text-neutral-400 mb-3">
              You're discoverable now. Search or tap a suggestion below to follow your first cricketer.
            </div>
            <button
              data-testid="pulse-post-setup-dismiss"
              onClick={() => setJustCreated(false)}
              className="text-[12px] text-emerald-400 hover:text-emerald-300"
            >
              Got it
            </button>
          </div>
        )}

        {/* Existing profile shortcut removed — the permanent
            `pulse-profile-cta` in the header is now the single
            source of truth for profile access. */}

        <ShareComposer prefill={prefill} onShared={() => setReloadTs(Date.now())} />

        {/* Iter XX.272 — Gully Challenge #001 discovery card. Pulse
            is the primary discovery surface. Collapses to null when
            both gates (feature flag + challenge.enabled) don't
            permit exposure. */}
        <ChallengeCard state={challengeState} />

        <PeopleDiscovery onCreateProfile={() => setEditing(true)} />

        {feed.is_discover && feed.items.length > 0 && (
          <div
            data-testid="pulse-discover-note"
            className="text-[12px] text-neutral-500 mb-3 uppercase tracking-widest"
          >
            Discover · cricket from around Gully
          </div>
        )}

        {feed.items.length === 0 && (
          <div data-testid="pulse-empty" className="text-neutral-500 text-[14px] py-10 text-center">
            No cricket moments yet. Share yours to get the feed rolling.
          </div>
        )}

        {feed.items.map((m) => (
          <MomentCard
            key={m.moment_id}
            item={m}
            onDelete={async (id) => { await deleteMoment(id); setReloadTs(Date.now()); }}
            onCelebrated={() => { /* count updates in-card */ }}
            onOpenProfile={openProfile}
          />
        ))}
      </div>
      {/* Iter XX.267a — Mobile bottom nav. Renders only on narrow
          widths (`sm:hidden` inside PrimarySurfaceNav is not needed —
          MobileBottomNav is fixed-positioned and its parent is already
          gated to primary surfaces). */}
      <div className="sm:hidden">
        <PrimarySurfaceNav currentRoute="pulse" />
      </div>
    </div>
  );
}
